import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from './config/api';
import { authFetch, MIN_PASSWORD_LENGTH } from './config/auth';
import useFocusTrap from './useFocusTrap';
import ConfirmDialog from './ConfirmDialog';
import AvButton from './design/nedi/AvButton';
import AvField from './design/nedi/AvField';
import { NediPanel, NediScreen } from './design/nedi/NediLayout';
import { mountNediTheme } from './design/nedi/mountNediTheme';
import './design/atlasVivo/tokens.css';
import './design/atlasVivo/Buttons.css';
import './LoginModal.css';
import { t } from './i18n';

function formatCreatedAt(value) {
  if (!value) return '—';
  const raw = String(value);
  // SQLite datetime('now') → "YYYY-MM-DD HH:MM:SS"
  const datePart = raw.slice(0, 10);
  return datePart || raw;
}

function submitClosestForm(e) {
  const form = e.currentTarget.closest('form');
  if (form?.requestSubmit) form.requestSubmit();
  else form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
}

function UsersAdminModal({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const containerRef = useFocusTrap(true, onClose);

  useEffect(() => {
    mountNediTheme();
  }, []);

  const loadUsers = useCallback(async () => {
    setErrorMessage('');
    setLoading(true);
    try {
      const response = await authFetch(apiUrl('/api/admin/users'));
      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.error || t('web.users.listFail'));
        return;
      }
      setUsers(data.users || []);
    } catch {
      setErrorMessage(t('web.account.serverError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setTempPassword(null);
    if (!username.trim() || !password) {
      setErrorMessage(t('web.users.needCreds'));
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(t('web.users.passwordMin', { n: MIN_PASSWORD_LENGTH }));
      return;
    }
    setCreating(true);
    try {
      const response = await authFetch(apiUrl('/api/admin/users'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.error || t('web.users.createFail'));
        return;
      }
      setUsername('');
      setPassword('');
      await loadUsers();
    } catch {
      setErrorMessage(t('web.account.serverError'));
    } finally {
      setCreating(false);
    }
  };

  const handleReset = async (user) => {
    setErrorMessage('');
    setTempPassword(null);
    try {
      const response = await authFetch(
        apiUrl(`/api/admin/users/${user.id}/reset-password`),
        { method: 'POST' },
      );
      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.error || t('web.users.resetFail'));
        return;
      }
      setTempPassword({
        username: user.username,
        password: data.temporary_password,
      });
    } catch {
      setErrorMessage(t('web.account.serverError'));
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const user = pendingDelete;
    setPendingDelete(null);
    setErrorMessage('');
    setTempPassword(null);
    try {
      const response = await authFetch(apiUrl(`/api/admin/users/${user.id}`), {
        method: 'DELETE',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setErrorMessage(data.error || t('web.users.deleteFail'));
        return;
      }
      await loadUsers();
    } catch {
      setErrorMessage(t('web.account.serverError'));
    }
  };

  return (
    <div className="login-modal-overlay" onClick={onClose} role="presentation">
      {/* Package CEs: deepen 5.2 Timeline React — UsersAdmin beyond login-modal-content. */}
      <NediScreen className="login-nedi-screen login-nedi-screen--users">
        <div
          className="login-dialog"
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="users-admin-title"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="login-header">
            <h1 id="users-admin-title">Usuários</h1>
          </header>

          <NediPanel className="login-nedi-panel">
            {loading ? (
              <p>{t('label.loading')}</p>
            ) : (
              <table className="users-admin-table">
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Papel</th>
                    <th>Criado</th>
                    <th className="users-admin-table-actions">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.role}</td>
                      <td>{formatCreatedAt(user.created_at)}</td>
                      <td className="users-admin-table-actions">
                        <div className="login-actions-row users-admin-row-actions">
                          <AvButton type="button" variant="secondary" onClick={() => handleReset(user)}>
                            {t('web.users.resetPassword')}
                          </AvButton>
                          <AvButton type="button" variant="destructive" onClick={() => setPendingDelete(user)}>
                            {t('web.users.remove')}
                          </AvButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <form onSubmit={handleCreate} className="login-form">
              <AvField
                name="users-admin-username"
                label={t('web.users.newViewer')}
                type="text"
                value={username}
                onChange={setUsername}
                autoComplete="off"
                minLength={3}
                required
              />
              <AvField
                name="users-admin-password"
                label="Senha"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                required
              />
              <AvButton
                type="button"
                variant="primary"
                disabled={creating}
                loading={creating}
                block
                onClick={submitClosestForm}
              >
                {creating ? 'Criando…' : 'Criar viewer'}
              </AvButton>
            </form>

            {tempPassword && (
              <div className="login-success-msg" role="status">
                Senha temporária para <strong>{tempPassword.username}</strong>:{' '}
                <code>{tempPassword.password}</code>
              </div>
            )}

            {errorMessage && (
              <div className="login-error-msg" role="alert">
                {errorMessage}
              </div>
            )}

            <div className="login-actions-row">
              <AvButton type="button" variant="secondary" onClick={onClose}>
                {t('action.close')}
              </AvButton>
            </div>
          </NediPanel>
        </div>
      </NediScreen>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={t('web.users.removeTitle')}
        message={
          pendingDelete
            ? t('web.users.removeConfirm', { user: pendingDelete.username })
            : ''
        }
        confirmLabel={t('web.users.remove')}
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

export default UsersAdminModal;
export { formatCreatedAt };
